import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Collapse, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api.js";
import Swal from 'sweetalert2';
import {
    FaThumbsUp,
    FaCheckCircle,
    FaArrowDown,
    FaChevronDown,
    FaChevronUp,
    FaFilter,
} from "react-icons/fa";
import Menu from "../../comum/Menu/index.jsx";
import Footer from "../../comum/Footer/index.jsx";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Line,
    LineChart,
    LabelList,
    ResponsiveContainer,
} from "recharts";

// Modal de Loading
function LoadingModal({ show, message }) {
    if (!show) return null;
    return (
        <div className="loading-modal-overlay">
            <div className="loading-modal-content">
                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="visually-hidden">Loading...</span>
                </div>
                <div style={{ marginTop: 15, fontSize: 18 }}>{message}</div>
            </div>
            <style>{`
                .loading-modal-overlay {
                    position: fixed;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(0,0,0,0.5);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 1050;
                }
                .loading-modal-content {
                    background: white;
                    padding: 20px 40px;
                    border-radius: 8px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    box-shadow: 0 0 10px rgba(0,0,0,0.25);
                }
            `}</style>
        </div>
    );
}

// Formata moeda BR
const formatMoeda = (valor) =>
    valor?.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// Formata percentual BR
const formatPercent = (valor) => {
    if (isNaN(valor) || valor === null || valor === undefined) {
        return "N/A";
    }
    return `${Number(valor).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })} %`;
};

const VendaComparativo = () => {
    const [showModal, setShowModal] = useState(true);
    const [showLojaModal, setShowLojaModal] = useState(false);
    const [mesSelecionado, setMesSelecionado] = useState("");
    const [lojas, setLojas] = useState([]);

    const [loja1, setLoja1] = useState(null);
    const [loja2, setLoja2] = useState(null);
    const [dadosLoja1, setDadosLoja1] = useState([]);
    const [dadosLoja2, setDadosLoja2] = useState([]);

    const [detalhesDia, setDetalhesDia] = useState({});
    const [loading, setLoading] = useState(false);
    
    const navigate = useNavigate();

    // Verifica acesso
    useEffect(() => {
        const usuario = localStorage.getItem("chapa");
        const permiteVendas = localStorage.getItem("permiteVendas");

        if (!usuario) {
            navigate("/arbor/login");
            return;
        }

        if (permiteVendas !== "S") {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Não é permitido acessar as vendas.',
                confirmButtonText: 'OK'
            }).then(() => {
                navigate("/arbor/dadosfunc");
            });
        }
    }, [navigate]);

    // Mês atual
    useEffect(() => {
        const hoje = new Date();
        const aa = String(hoje.getFullYear()).slice(2);
        const mm = String(hoje.getMonth() + 1).padStart(2, "0");
        setMesSelecionado(`${aa}${mm}`);
    }, []);

    // Carregar lista de lojas
    useEffect(() => {
        const fetchLojas = async () => {
            try {
                const { data } = await api.get('/lojas/localizacao');
                setLojas(data);
            } catch (e) {
                console.error("Erro ao carregar lojas:", e);
                setLojas([]);
            }
        };
        fetchLojas();
    }, []);

    const carregarDadosComparacao = async () => {
        const usuario = localStorage.getItem("chapa");
        if (!mesSelecionado || !usuario || !loja1 || !loja2) return;

        setLoading(true);
        setDadosLoja1([]);
        setDadosLoja2([]);

        try {
            const { data: dataLoja1 } = await api.get(
                `venda/mes/acum?mes=${mesSelecionado}&usuario=${usuario}&busca=${loja1}`
            );
            const { data: dataLoja2 } = await api.get(
                `venda/mes/acum?mes=${mesSelecionado}&usuario=${usuario}&busca=${loja2}`
            );

            setDadosLoja1(Array.isArray(dataLoja1) ? dataLoja1 : []);
            setDadosLoja2(Array.isArray(dataLoja2) ? dataLoja2 : []);

            setDetalhesDia({}); // Limpa os detalhes do dia ao recarregar
        } catch (e) {
            console.error("Erro ao carregar dados para comparação:", e);
        }
        setLoading(false);
    };

    const carregarDia = async (dia, numdia) => {
        const usuario = localStorage.getItem("chapa");
        if (!usuario || !loja1 || !loja2) return;

        setLoading(true);

        if (detalhesDia[numdia]) {
            const copia = { ...detalhesDia };
            delete copia[numdia];
            setDetalhesDia(copia);
            setLoading(false);
            return;
        }
        
        try {
            const { data: dataDetalhesLoja1 } = await api.get(
                `venda/mes/loja?mes=${mesSelecionado}&usuario=${usuario}&dia=${dia}&busca=${loja1}`
            );
            const { data: dataDetalhesLoja2 } = await api.get(
                `venda/mes/loja?mes=${mesSelecionado}&usuario=${usuario}&dia=${dia}&busca=${loja2}`
            );

            // Combina os dados de ambas as lojas para o dia
            const detalhesDiaCombinado = [
                ...(Array.isArray(dataDetalhesLoja1) ? dataDetalhesLoja1 : []),
                ...(Array.isArray(dataDetalhesLoja2) ? dataDetalhesLoja2 : []),
            ];

            setDetalhesDia({ ...detalhesDia, [numdia]: detalhesDiaCombinado });
        } catch (e) {
            console.error("Erro carregando dia:", e);
        }
        setLoading(false);
    };
    
    // UseEffect para carregar os dados quando as lojas ou o mês mudarem
    useEffect(() => {
        if (mesSelecionado && loja1 && loja2) {
            carregarDadosComparacao();
        }
    }, [mesSelecionado, loja1, loja2]);


    const handleMesChange = (valor) => {
        setMesSelecionado("");
        if (!valor) return;
        const [ano, mes] = valor.split("-");
        const aa = ano.slice(2);
        setMesSelecionado(`${aa}${mes}`);
    };

    const chartHeight = 300;

    const combinedData = dadosLoja1.map((d1, i) => ({
        numdia: d1.numdia,
        vendaLoja1: d1.venda,
        metaLoja1: d1.meta,
        margemLoja1: d1.margem,
        atingidoLoja1: d1.atingido,
        atingidoperLoja1: d1.atingidoper,

        vendaLoja2: dadosLoja2[i]?.venda,
        metaLoja2: dadosLoja2[i]?.meta,
        margemLoja2: dadosLoja2[i]?.margem,
        atingidoLoja2: dadosLoja2[i]?.atingido,
        atingidoperLoja2: dadosLoja2[i]?.atingidoper,
    }));

    // Calculando os totais
    const totalVendaLoja1 = dadosLoja1.reduce((acc, curr) => acc + (curr.venda || 0), 0);
    const totalMetaLoja1 = dadosLoja1.reduce((acc, curr) => acc + (curr.meta || 0), 0);
    const totalMargemLoja1 = totalVendaLoja1 > 0 ? (dadosLoja1.reduce((acc, curr) => acc + ((curr.margem / 100) * curr.venda || 0), 0) / totalVendaLoja1) * 100 : 0;
    const totalAtingidoLoja1 = totalMetaLoja1 > 0 ? (totalVendaLoja1 / totalMetaLoja1) * 100 : 0;
    const totalAtingidoPerLoja1 = dadosLoja1.length > 0 ? (dadosLoja1.at(-1)?.atingidoper || 0) : 0;

    const totalVendaLoja2 = dadosLoja2.reduce((acc, curr) => acc + (curr.venda || 0), 0);
    const totalMetaLoja2 = dadosLoja2.reduce((acc, curr) => acc + (curr.meta || 0), 0);
    const totalMargemLoja2 = totalVendaLoja2 > 0 ? (dadosLoja2.reduce((acc, curr) => acc + ((curr.margem / 100) * curr.venda || 0), 0) / totalVendaLoja2) * 100 : 0;
    const totalAtingidoLoja2 = totalMetaLoja2 > 0 ? (totalVendaLoja2 / totalMetaLoja2) * 100 : 0;
    const totalAtingidoPerLoja2 = dadosLoja2.length > 0 ? (dadosLoja2.at(-1)?.atingidoper || 0) : 0;


    return (
        <div>
            <Menu modulo="ARBOR" />
            <div className="p-3 container-fluid">
                <h3 className="text-center mb-3">
                    Comparativo de Vendas - Mês {mesSelecionado}
                </h3>

                {/* Modal Seleção Mês */}
                <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Selecionar Mês</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <input
                            type="month"
                            className="form-control"
                            value={mesSelecionado ? `${"20" + mesSelecionado.slice(0, 2)}-${mesSelecionado.slice(2)}` : ''}
                            onChange={(e) => handleMesChange(e.target.value)}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Fechar
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => setShowModal(false)}
                        >
                            Carregar
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal Seleção de Lojas */}
                <Modal show={showLojaModal} onHide={() => setShowLojaModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Selecionar Lojas para Comparar</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group controlId="selectLoja1" className="mb-3">
                            <Form.Label>Loja 1 (Vermelho)</Form.Label>
                            <Form.Control
                                as="select"
                                value={loja1 || ''}
                                onChange={(e) => setLoja1(e.target.value)}
                            >
                                <option value="">Selecione a Loja 1</option>
                                {lojas.map(loja => (
                                    <option key={loja.cod_emp} value={String(loja.cod_emp).padStart(2, '0')}>
                                        Filial {String(loja.cod_emp).padStart(2, '0')} - {loja.razao}
                                    </option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                        <Form.Group controlId="selectLoja2">
                            <Form.Label>Loja 2 (Verde)</Form.Label>
                            <Form.Control
                                as="select"
                                value={loja2 || ''}
                                onChange={(e) => setLoja2(e.target.value)}
                            >
                                <option value="">Selecione a Loja 2</option>
                                {lojas.map(loja => (
                                    <option key={loja.cod_emp} value={String(loja.cod_emp).padStart(2, '0')}>
                                        Filial {String(loja.cod_emp).padStart(2, '0')} - {loja.razao}
                                    </option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowLojaModal(false)}>
                            Fechar
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => {
                                setShowLojaModal(false);
                                carregarDadosComparacao();
                            }}
                            disabled={!loja1 || !loja2}
                        >
                            Comparar
                        </Button>
                    </Modal.Footer>
                </Modal>

                <div className="mb-3 d-flex flex-column flex-sm-row gap-2 align-items-center">
                    <label className="me-2 mb-0">Mês/Ano:</label>
                    <input
                        type="month"
                        className="form-control"
                        style={{ maxWidth: '200px' }}
                        value={mesSelecionado ? `${"20" + mesSelecionado.slice(0, 2)}-${mesSelecionado.slice(2)}` : ''}
                        onChange={(e) => handleMesChange(e.target.value)}
                    />
                    <div className="d-flex gap-2">
                        <Button
                            onClick={() => carregarDadosComparacao()}
                            disabled={loading || !loja1 || !loja2}
                        >
                            {loading ? "Carregando..." : "Buscar"}
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => setShowLojaModal(true)}
                        >
                            <FaFilter className="me-1" /> Lojas
                        </Button>
                    </div>
                </div>

                {/* Gráficos */}
                {combinedData.length > 0 ? (
                    <div className="chart-container">
                        {/* Gráfico 1 - Meta atingida */}
                        <h5 className="mt-4">Meta atingida (%)</h5>
                        <ResponsiveContainer width="100%" height={chartHeight}>
                            <BarChart data={combinedData} margin={{ top: 30, right: 10, left: 0, bottom: 50 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="numdia"
                                    interval={0}
                                    angle={-90}
                                    textAnchor="end"
                                    height={50}
                                    style={{ fontSize: 10 }}
                                />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip formatter={(value, name) => [`${formatPercent(value)}`, name]} />
                                <Legend wrapperStyle={{ fontSize: "10px" }} />
                                <Bar dataKey="atingidoLoja1" name={`Loja ${loja1}`} fill="#d9534f" />
                                <Bar dataKey="atingidoLoja2" name={`Loja ${loja2}`} fill="#5cb85c" />
                            </BarChart>
                        </ResponsiveContainer>

                        {/* Gráfico 2 - Evolução Margem */}
                        <h5 className="mt-4">Evolução Margem (%)</h5>
                        <ResponsiveContainer width="100%" height={chartHeight}>
                            <LineChart data={combinedData} margin={{ top: 30, right: 10, left: 0, bottom: 50 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="numdia"
                                    interval={0}
                                    angle={-90}
                                    textAnchor="end"
                                    height={50}
                                    style={{ fontSize: 10 }}
                                />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip formatter={(value, name) => [`${formatPercent(value)}`, name]} />
                                <Legend wrapperStyle={{ fontSize: "10px" }} />
                                <Line type="monotone" dataKey="margemLoja1" name={`Loja ${loja1}`} stroke="#d9534f" />
                                <Line type="monotone" dataKey="margemLoja2" name={`Loja ${loja2}`} stroke="#5cb85c" />
                            </LineChart>
                        </ResponsiveContainer>

                        

                        {/* Gráfico 4 - Meta atingida por período */}
                        <h5 className="mt-4">Meta atingida por período (%)</h5>
                        <ResponsiveContainer width="100%" height={chartHeight}>
                            <LineChart data={combinedData} margin={{ top: 30, right: 10, left: 0, bottom: 50 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="numdia"
                                    interval={0}
                                    angle={-90}
                                    textAnchor="end"
                                    height={50}
                                    style={{ fontSize: 10 }}
                                />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip formatter={(value, name) => [`${formatPercent(value)}`, name]} />
                                <Legend wrapperStyle={{ fontSize: "10px" }} />
                                <Line type="monotone" dataKey="atingidoperLoja1" name={`Loja ${loja1}`} stroke="#d9534f" />
                                <Line type="monotone" dataKey="atingidoperLoja2" name={`Loja ${loja2}`} stroke="#5cb85c" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                ) : (
                    <div className="text-center mt-5">
                        <p>Nenhum dado disponível para as lojas selecionadas.</p>
                    </div>
                )}

                {/* Tabela */}
                <div className="table-responsive mt-4">
                    <Table
                        striped
                        bordered
                        hover
                        size="sm"
                        className="text-center"
                    >
                        <thead>
                            <tr>
                                <th>Dia</th>
                                <th>Loja</th>
                                <th>Venda</th>
                                <th>Meta</th>
                                <th>Margem (%)</th>
                                <th>Atingido Dia</th>
                                <th>Atingido Período%</th>
                            </tr>
                        </thead>
                        <tbody>
                            {combinedData.length > 0 ? (
                                <>
                                    {combinedData.map((d, i) => (
                                        <React.Fragment key={d.numdia}>
                                            <tr>
                                                <td rowSpan="2">{d.numdia}</td>
                                                <td style={{ color: '#d9534f' }}>{loja1}</td>
                                                <td style={{ color: '#d9534f' }}>{formatMoeda(d.vendaLoja1)}</td>
                                                <td style={{ color: '#d9534f' }}>{formatMoeda(d.metaLoja1)}</td>
                                                <td style={{ color: '#d9534f' }}>{formatPercent(d.margemLoja1)}</td>
                                                <td style={{ color: '#d9534f' }}>
                                                    {formatPercent(d.atingidoLoja1)}
                                                    {d.atingidoLoja1 > 100 ? (
                                                        <FaThumbsUp className="text-success ms-2" />
                                                    ) : (
                                                        <FaArrowDown className="text-danger ms-2" />
                                                    )}
                                                </td>
                                                <td style={{ color: '#d9534f' }}>
                                                    {formatPercent(d.atingidoperLoja1)}
                                                    {d.atingidoperLoja1 > 100 ? (
                                                        <FaCheckCircle className="text-success ms-2" />
                                                    ) : (
                                                        <FaArrowDown className="text-danger ms-2" />
                                                    )}
                                                </td>
                                                
                                            </tr>
                                            <tr>
                                                <td style={{ color: '#5cb85c' }}>{loja2}</td>
                                                <td style={{ color: '#5cb85c' }}>{formatMoeda(d.vendaLoja2)}</td>
                                                <td style={{ color: '#5cb85c' }}>{formatMoeda(d.metaLoja2)}</td>
                                                <td style={{ color: '#5cb85c' }}>{formatPercent(d.margemLoja2)}</td>
                                                <td style={{ color: '#5cb85c' }}>
                                                    {formatPercent(d.atingidoLoja2)}
                                                    {d.atingidoLoja2 > 100 ? (
                                                        <FaThumbsUp className="text-success ms-2" />
                                                    ) : (
                                                        <FaArrowDown className="text-danger ms-2" />
                                                    )}
                                                </td>
                                                <td style={{ color: '#5cb85c' }}>
                                                    {formatPercent(d.atingidoperLoja2)}
                                                    {d.atingidoperLoja2 > 100 ? (
                                                        <FaCheckCircle className="text-success ms-2" />
                                                    ) : (
                                                        <FaArrowDown className="text-danger ms-2" />
                                                    )}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td colSpan="8" className="p-0 border-0">
                                                    <Collapse in={!!detalhesDia[d.numdia]}>
                                                        <div>
                                                            {detalhesDia[d.numdia] && (
                                                                <div className="table-responsive">
                                                                    <Table striped bordered hover size="sm" className="mb-0">
                                                                        <thead>
                                                                            <tr>
                                                                                <th>Filial</th>
                                                                                <th>Venda</th>
                                                                                <th>Meta</th>
                                                                                <th>Margem (%)</th>
                                                                                <th>Atingido</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {detalhesDia[d.numdia].map((loja, idx) => (
                                                                                <tr key={idx} style={{ color: loja.filial == loja1 ? '#d9534f' : '#5cb85c'}}>
                                                                                    <td>{String(loja.filial).padStart(2, "0")}</td>
                                                                                    <td>{formatMoeda(loja.venda)}</td>
                                                                                    <td>{formatMoeda(loja.meta)}</td>
                                                                                    <td>{formatPercent(loja.margem)}</td>
                                                                                    <td>{formatPercent(loja.atingido)}</td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </Table>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </Collapse>
                                                </td>
                                            </tr>
                                        </React.Fragment>
                                    ))}
                                    {/* Linha de totais */}
                                    <tr style={{ fontWeight: 'bold' }}>
                                        <td rowSpan="2">TOTAL</td>
                                        <td style={{ color: '#d9534f' }}>{loja1}</td>
                                        <td style={{ color: '#d9534f' }}>{formatMoeda(totalVendaLoja1)}</td>
                                        <td style={{ color: '#d9534f' }}>{formatMoeda(totalMetaLoja1)}</td>
                                        <td style={{ color: '#d9534f' }}>{formatPercent(totalMargemLoja1)}</td>
                                        <td style={{ color: '#d9534f' }}>{formatPercent(totalAtingidoLoja1)}</td>
                                        <td style={{ color: '#d9534f' }}>{formatPercent(totalAtingidoPerLoja1)}</td>
                                    </tr>
                                    <tr style={{ fontWeight: 'bold' }}>
                                        <td style={{ color: '#5cb85c' }}>{loja2}</td>
                                        <td style={{ color: '#5cb85c' }}>{formatMoeda(totalVendaLoja2)}</td>
                                        <td style={{ color: '#5cb85c' }}>{formatMoeda(totalMetaLoja2)}</td>
                                        <td style={{ color: '#5cb85c' }}>{formatPercent(totalMargemLoja2)}</td>
                                        <td style={{ color: '#5cb85c' }}>{formatPercent(totalAtingidoLoja2)}</td>
                                        <td style={{ color: '#5cb85c' }}>{formatPercent(totalAtingidoPerLoja2)}</td>
                                    </tr>
                                </>
                            ) : (
                                <tr>
                                    <td colSpan="8">Selecione duas lojas para iniciar a comparação.</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>
            <Footer className="footer navbar-fixed-bottom" />
            <LoadingModal show={loading} message="Carregando..." />
        </div>
    );
};

export default VendaComparativo;